import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled/macro';
import Grid from 'components/Grid';
import Form from 'components/FinalForm/Form';
import FormField from 'components/FinalForm/Fields/FormField';
import Button from 'components/Button';
import Collapse from '@mui/material/Collapse';
import { useDispatch, useSelector } from 'react-redux';
import { pulseActions, pulseSelectors } from 'redux/pulse';
import { authSelectors } from 'redux/auth';
import validator, { isRequired } from 'validators';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useFeature from 'hooks/useFeature';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import IconText from 'components/IconText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { css } from '@emotion/react';
import PulseScaleField from './PulseScaleField';

const Wrapper = styled('div')`
  ${({ disablePadding }) => !disablePadding && css`
    padding: 2.5rem 0;
  `}
`;

const SmallText = styled(IconText)`
  color: #666666;
  margin: 1rem 0;
  display: flex;
  justify-content: center;
  white-space: initial;
  text-align: center;
`;

const DisclaimerText = styled(IconText)`
  color: #666666;
  margin: 1rem 0;
  display: flex;
  justify-content: center;
  white-space: initial;
  text-align: center;
  align-items: flex-start;
`;

const BigText = styled('div')`
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
`;

const PreviewText = styled('div')`
  color: #adabab;
  font-size: 1.6rem;
  font-weight: 400;
  text-align: center;
`;

const FormFieldWrapper = styled('div')`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const FormFieldWrapperInner = styled('div')`
  max-width: 500px;
  width: 100%;
`;

const StyledFormField = styled(FormField)`
  textarea:not(:focus) {
    &::-webkit-input-placeholder {
      opacity: 1;
    }
    &::-moz-placeholder {
      opacity: 1;
    }
    &:-ms-input-placeholder {
      opacity: 1;
    }
    &::-ms-input-placeholder {
      opacity: 1;
    }
  }
`;

const ActivePulseSurveyForm = ({ onFinish = () => { }, previewPulseSurvey, disablePadding, ...rest }) => {
  const dispatch = useDispatch();

  const thanksRef = useRef();

  const pulseFeature = useFeature('pulse');

  const { t } = useTranslation(['component', 'pulse']);

  const workspace = useSelector(authSelectors.getWorkspace);

  const pulseSurveySelector = useSelector(pulseSelectors.getPulseSurveySelector);
  const feedActivePulseSurveyId = useSelector(pulseSelectors.getActivePulseSurveySelector);
  const activePulseSurvey = previewPulseSurvey || pulseSurveySelector(feedActivePulseSurveyId);
  const pulseScaleId = activePulseSurvey?.pulseScale?.id || activePulseSurvey?.pulseScale;
  const pulseScale = useSelector(pulseSelectors.getPulseScales).find(scale => pulseScaleId === scale.id);

  const [visible, setVisible] = useState(false);

  const smallScreen = useMediaQuery('(max-width: 400px)');

  useEffect(() => {
    if (!previewPulseSurvey) {
      dispatch(pulseActions.getActivePulseSurvey());
    }
  }, [dispatch, previewPulseSurvey]);

  // Display fast if required. Disappear slow
  // Disappearance is likely due to answering... So fade away
  useEffect(() => {
    if (activePulseSurvey) {
      setVisible(true);
      return;
    }

    if (!workspace) {
      return;
    }

    if (visible) {
      const fn = () => {
        setVisible(false);
        onFinish();
      };
      const timeout = setTimeout(fn, 2000);
      return () => clearTimeout(timeout);
    }

    onFinish();
  }, [onFinish, visible, workspace, activePulseSurvey]);

  if (!pulseFeature.enabled) {
    return null;
  }

  const onSubmit = values => {
    if (previewPulseSurvey) {
      return;
    }

    dispatch(pulseActions.storePulseSurveyResponse({ ...values, pulseSurveyId: activePulseSurvey.id }));
    if (thanksRef.current) {
      thanksRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  const commentIsRequired = selectedPulseScaleOption => (activePulseSurvey?.pulseScaleOptionsRequiringComment || []).includes(selectedPulseScaleOption);

  const validate = validator(values => ({
    selectedPulseScaleOption: [isRequired],
    comment: commentIsRequired(values.selectedPulseScaleOption) ? [isRequired] : [],
  }));

  return (
    <Collapse in={visible} unmountOnExit {...rest}>
      <Form onSubmit={onSubmit} validate={validate}>
        {({ handleSubmit, values, submitting, submitSucceeded }) => (
          <Wrapper disablePadding={disablePadding}>
            <Collapse in={submitSucceeded && !previewPulseSurvey} ref={thanksRef}>
              <BigText>{t('component:activePulseSurveyForm.thanksForResponse')}</BigText>
            </Collapse>
            <Collapse component="form" in={!submitSucceeded || !!previewPulseSurvey} onSubmit={handleSubmit}>
              {
              activePulseSurvey && (
                <Grid
                  container
                  direction="column"
                  wrap="nowrap"
                  spacing={['faces', 'thumbs'].includes(pulseScale?.type) ? 0 : 3}
                >
                  <Grid item>
                    <SmallText>
                      {t('component:activePulseSurveyForm.introSubText', { workspace: workspace.name })}
                    </SmallText>
                    {previewPulseSurvey && !activePulseSurvey.question && (
                      <PreviewText>{t('pulse:pulseSurveyForm.questionPlaceholderInPreview')}</PreviewText>
                    )}
                    <BigText>{activePulseSurvey.question}</BigText>
                    <SmallText>{activePulseSurvey.body}</SmallText>
                  </Grid>

                  <Grid item>
                    {pulseScale && (
                      <PulseScaleField
                        pulseScale={pulseScale}
                        pulseScaleRangeType={activePulseSurvey.pulseScaleRangeType}
                        pulseScaleLabelGroup={activePulseSurvey.pulseScaleLabelGroup}
                        name="selectedPulseScaleOption"
                      />
                    )}
                  </Grid>

                  <Collapse in={!!values.selectedPulseScaleOption} component={Grid} item sx={{ mb: 1 }}>
                    <FormFieldWrapper>
                      <FormFieldWrapperInner>
                        <Grid container spacing={1} alignItems="center" justifyContent="center">
                          <Grid item xs={smallScreen ? 12 : true}>
                            <StyledFormField
                              name="comment"
                              variant="outlined"
                              margin="none"
                              size="medium"
                              multiline
                              maxRows={7}
                              minRows={1}
                              placeholder={`${t('component:activePulseSurveyForm.feedbackPlaceholder')} ${
                                commentIsRequired(values.selectedPulseScaleOption)
                                  ? `(${t('component:activePulseSurveyForm.commentIsRequired')})`
                                  : ''
                              }`}
                            />
                          </Grid>
                          <Grid item>
                            <Button
                              type="submit"
                              size="large"
                              disabled={
                            submitting || (!values.comment && commentIsRequired(values.selectedPulseScaleOption))
                          }
                            >
                              {t('component:activePulseSurveyForm.submit')}
                            </Button>
                          </Grid>
                        </Grid>
                      </FormFieldWrapperInner>
                    </FormFieldWrapper>
                  </Collapse>
                  <Grid item>
                    <DisclaimerText
                      startIcon={
                    activePulseSurvey.anonymityLevel === 'anonymous'
                      ? <LockOutlinedIcon fontSize="inherit" color="inherit" sx={{ mt: 0.4 }} />
                      : <LockOpenOutlinedIcon fontSize="inherit" color="inherit" sx={{ mt: 0.4 }} />
                  }
                    >
                      {activePulseSurvey.anonymityLevel === 'anonymous'
                    && t('component:activePulseSurveyForm.anonymousDisclaimer')}
                      {activePulseSurvey.anonymityLevel === 'department'
                    && t('component:activePulseSurveyForm.departmentDisclaimer', {
                      department: workspace.memberDepartment || t('component:activePulseSurveyForm.empty'),
                    })}
                      {activePulseSurvey.anonymityLevel === 'location'
                    && t('component:activePulseSurveyForm.locationDisclaimer', {
                      location: workspace.memberLocation || t('component:activePulseSurveyForm.empty'),
                    })}
                      {activePulseSurvey.anonymityLevel === 'identified'
                    && t('component:activePulseSurveyForm.identifiedDisclaimer')}
                    </DisclaimerText>
                  </Grid>
                </Grid>
              )
            }
            </Collapse>
          </Wrapper>
        )}
      </Form>
    </Collapse>
  );
};

ActivePulseSurveyForm.propTypes = {
  onFinish: PropTypes.func,
  previewPulseSurvey: PropTypes.object,
  disablePadding: PropTypes.bool,
};

export default ActivePulseSurveyForm;
